'use client';

import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { sanitizeHTML } from '@benzinga/frontend-utils';
import { SectionTitle, Button } from '@benzinga/core-ui';

import styles from './countDownCardBlock.styles.module.scss';

const DATE_TIME_ZONE = 'America/New_York';

export interface CountDownCardBlockProps {
  cta_label?: string;
  link: string;
  label: string;
  date_time?: string;
  description?: string;
  location?: 'sidebar' | 'default' | 'article-bottom' | 'article-in-content' | 'floating-widget' | string;
}

const getReturnValues = (countDown: number) => {
  if (countDown < 0) {
    return ['00', '00', '00', '00'] as const;
  }
  const days = String(Math.floor(countDown / (1000 * 60 * 60 * 24))).padStart(2, '0');
  const hours = String(Math.floor((countDown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))).padStart(2, '0');
  const minutes = String(Math.floor((countDown % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0');
  const seconds = String(Math.floor((countDown % (1000 * 60)) / 1000)).padStart(2, '0');

  return [days, hours, minutes, seconds] as const;
};

const useCountdown = (targetMs: number) => {
  const [remainingMs, setRemainingMs] = useState(() => targetMs - Date.now());

  useEffect(() => {
    const tick = () => setRemainingMs(targetMs - Date.now());
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [targetMs]);

  return remainingMs;
};

const DateTimeDisplay = ({ type, value }: { type: string; value: string }) => (
  <div className={styles.countdown}>
    <p>{value}</p>
    <span>{type}</span>
  </div>
);

const CountdownTicker = ({ endMs }: { endMs: number }) => {
  const remainingMs = useCountdown(endMs);
  const [days, hours, minutes, seconds] = getReturnValues(remainingMs);

  if (remainingMs <= 0) {
    return null;
  }

  return (
    <div className={styles.counterSection}>
      <div className={styles.countdownWrap}>
        <DateTimeDisplay type="Days" value={days} />
        <p>:</p>
        <DateTimeDisplay type="Hours" value={hours} />
        <p>:</p>
        <DateTimeDisplay type="Mins" value={minutes} />
        <p>:</p>
        <DateTimeDisplay type="Seconds" value={seconds} />
      </div>
    </div>
  );
};

export const CountDownCardBlock: React.FC<CountDownCardBlockProps> = ({
  cta_label,
  date_time,
  description,
  label,
  link,
  location,
}) => {
  const [endMs, setEndMs] = useState(0);

  useEffect(() => {
    if (date_time) {
      const raw = date_time.trim();
      const parseOpts = { zone: DATE_TIME_ZONE } as const;
      let end = DateTime.fromFormat(raw, 'd/M/yyyy h:mm a', parseOpts);
      if (!end.isValid) {
        end = DateTime.fromFormat(raw, 'd/M/yyyy h:m a', parseOpts);
      }
      if (!end.isValid) {
        setEndMs(0);
        return;
      }
      const t = end.toMillis();
      if (t > Date.now()) {
        setEndMs(t);
      } else {
        setEndMs(0);
      }
    }
  }, [date_time]);

  const isCompactMode = location === 'sidebar' || location === 'floating-widget';

  const cta = link ? (
    <div className={isCompactMode ? styles.actionBtnSidebar : styles.actionBtn}>
      <Button as="a" href={link} size="md" variant="flat-orange">
        {cta_label ?? 'Get Started'}
      </Button>
    </div>
  ) : null;

  const body = (
    <>
      {label && (
        <SectionTitle capitalize={false} className="pb-3" level={2} uppercase={false}>
          {label}
        </SectionTitle>
      )}
      {date_time && endMs > 0 ? <CountdownTicker endMs={endMs} /> : null}
      {description && (
        <div className={styles.description} dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }} />
      )}
    </>
  );

  return (
    <div className={`${styles.wrapper} ${isCompactMode ? styles.wrapperSidebar : ''}`}>
      {isCompactMode ? (
        <div className={styles.stack}>
          <div className={styles.content}>{body}</div>
          {cta}
        </div>
      ) : (
        <div className={styles.row}>
          <div className={styles.contentMain}>{body}</div>
          {cta}
        </div>
      )}
    </div>
  );
};
